import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Building, Plane } from 'lucide-react';
import {
  initializeUrbanrideApi,
  isConfigured,
  searchLocations,
  searchAirlines,
  validateFlight,
  getRates,
  bookTrip,
  formatDateTime,
  createAirportLocation,
  createAddressLocation,
  createAsDirectedLocation,
  createPassenger,
  TITLES
} from '../../services/urbanrideService';
import { getApiBase, getAuthHeaders } from '../../utils/api';
import axios from 'axios';

const ChauffeuredTransferBooking = () => {
  const navigate = useNavigate();

  // API Configuration state
  const [apiConfigured, setApiConfigured] = useState(false);
  const [apiMode, setApiMode] = useState('test');
  const [configLoading, setConfigLoading] = useState(true);

  // Feature flag state
  const [featureEnabled, setFeatureEnabled] = useState(null); // null = loading

  // Form step state
  const [step, setStep] = useState(1); // 1: Form, 2: Select Vehicle, 3: Confirmation

  // Pickup state
  const [pickupType, setPickupType] = useState('airport'); // 'airport' or 'address'
  const [pickupLocation, setPickupLocation] = useState(null);
  const [pickupSearch, setPickupSearch] = useState('');
  const [pickupSearchResults, setPickupSearchResults] = useState([]);
  const [pickupSearchLoading, setPickupSearchLoading] = useState(false);
  const [showPickupDropdown, setShowPickupDropdown] = useState(false);
  const [pickupAddressDateTime, setPickupAddressDateTime] = useState('');

  // Airport pickup details
  const [pickupAirline, setPickupAirline] = useState('');
  const [pickupAirlineCode, setPickupAirlineCode] = useState('');
  const [pickupFlightNumber, setPickupFlightNumber] = useState('');
  const [pickupDate, setPickupDate] = useState('');
  const [pickupTime, setPickupTime] = useState('');
  const [pickupOriginAirport, setPickupOriginAirport] = useState('');
  const [pickupOriginAirportCode, setPickupOriginAirportCode] = useState('');
  const [airlineSearchResults, setAirlineSearchResults] = useState([]);
  const [showAirlineDropdown, setShowAirlineDropdown] = useState(false);
  const [airlineSearchLoading, setAirlineSearchLoading] = useState(false);
  // Origin airport autocomplete state
  const [originAirportSearchResults, setOriginAirportSearchResults] = useState([]);
  const [showOriginAirportDropdown, setShowOriginAirportDropdown] = useState(false);
  const [originAirportSearchLoading, setOriginAirportSearchLoading] = useState(false);

  // FBO pickup state (when pickupType === 'fbo')
  const [pickupFboAirport, setPickupFboAirport] = useState(null); // Selected airport for FBO
  const [pickupFboAirportSearch, setPickupFboAirportSearch] = useState('');
  const [pickupFboAirportResults, setPickupFboAirportResults] = useState([]);
  const [showPickupFboAirportDropdown, setShowPickupFboAirportDropdown] = useState(false);
  const [pickupFboAirportLoading, setPickupFboAirportLoading] = useState(false);
  const [pickupFbo, setPickupFbo] = useState(null); // Selected FBO
  const [pickupFboList, setPickupFboList] = useState([]); // FBOs at selected airport
  const [pickupFboSearch, setPickupFboSearch] = useState('');
  const [showPickupFboDropdown, setShowPickupFboDropdown] = useState(false);
  const [pickupFboLoading, setPickupFboLoading] = useState(false);
  const [pickupTailNumber, setPickupTailNumber] = useState('');
  const [pickupFboDateTime, setPickupFboDateTime] = useState('');

  // Dropoff state
  const [dropoffType, setDropoffType] = useState('address'); // 'airport' or 'address'
  const [dropoffLocation, setDropoffLocation] = useState(null);
  const [dropoffSearch, setDropoffSearch] = useState('');
  const [dropoffSearchResults, setDropoffSearchResults] = useState([]);
  const [dropoffSearchLoading, setDropoffSearchLoading] = useState(false);
  const [showDropoffDropdown, setShowDropoffDropdown] = useState(false);

  // Airport dropoff details
  const [dropoffAirline, setDropoffAirline] = useState('');
  const [dropoffAirlineCode, setDropoffAirlineCode] = useState('');
  const [dropoffFlightNumber, setDropoffFlightNumber] = useState('');
  const [dropoffDate, setDropoffDate] = useState('');
  const [dropoffTime, setDropoffTime] = useState('');
  const [dropoffDestinationAirport, setDropoffDestinationAirport] = useState('');
  const [dropoffDestinationAirportCode, setDropoffDestinationAirportCode] = useState('');
  const [dropoffAirlineSearchResults, setDropoffAirlineSearchResults] = useState([]);
  const [showDropoffAirlineDropdown, setShowDropoffAirlineDropdown] = useState(false);
  const [dropoffAirlineSearchLoading, setDropoffAirlineSearchLoading] = useState(false);
  // Destination airport autocomplete state
  const [destinationAirportSearchResults, setDestinationAirportSearchResults] = useState([]);
  const [showDestinationAirportDropdown, setShowDestinationAirportDropdown] = useState(false);
  const [destinationAirportSearchLoading, setDestinationAirportSearchLoading] = useState(false);

  // FBO dropoff state (when dropoffType === 'fbo')
  const [dropoffFboAirport, setDropoffFboAirport] = useState(null);
  const [dropoffFboAirportSearch, setDropoffFboAirportSearch] = useState('');
  const [dropoffFboAirportResults, setDropoffFboAirportResults] = useState([]);
  const [showDropoffFboAirportDropdown, setShowDropoffFboAirportDropdown] = useState(false);
  const [dropoffFboAirportLoading, setDropoffFboAirportLoading] = useState(false);
  const [dropoffFbo, setDropoffFbo] = useState(null);
  const [dropoffFboList, setDropoffFboList] = useState([]);
  const [dropoffFboSearch, setDropoffFboSearch] = useState('');
  const [showDropoffFboDropdown, setShowDropoffFboDropdown] = useState(false);
  const [dropoffFboLoading, setDropoffFboLoading] = useState(false);
  const [dropoffTailNumber, setDropoffTailNumber] = useState('');
  const [dropoffFboDateTime, setDropoffFboDateTime] = useState('');

  // Passenger state
  const [passenger, setPassenger] = useState({
    title: '',
    firstName: '',
    lastName: '',
    email: '',
    mobile: '',
    vip: false
  });

  // Comments
  const [clientComments, setClientComments] = useState('');

  // Stops state - additional stops between pickup and dropoff
  const [stops, setStops] = useState([]); // Array of {type, location, search, duration, searchResults, showDropdown, searchLoading}

  // Stop type options
  const STOP_TYPES = [
    { value: 'address', label: 'Address' },
    { value: 'airport', label: 'Airport' },
    { value: 'train_station', label: 'Train Station' },
    { value: 'fbo', label: 'FBO (Private Aviation)' },
    { value: 'as_directed', label: 'As Directed' }
  ];

  // Duration options in 15-minute increments (0 to 4 hours)
  const DURATION_OPTIONS = [
    { value: '', label: 'No wait time' },
    { value: '15', label: '15 minutes' },
    { value: '30', label: '30 minutes' },
    { value: '45', label: '45 minutes' },
    { value: '60', label: '1 hour' },
    { value: '75', label: '1 hour 15 min' },
    { value: '90', label: '1 hour 30 min' },
    { value: '105', label: '1 hour 45 min' },
    { value: '120', label: '2 hours' },
    { value: '135', label: '2 hours 15 min' },
    { value: '150', label: '2 hours 30 min' },
    { value: '165', label: '2 hours 45 min' },
    { value: '180', label: '3 hours' },
    { value: '195', label: '3 hours 15 min' },
    { value: '210', label: '3 hours 30 min' },
    { value: '225', label: '3 hours 45 min' },
    { value: '240', label: '4 hours' }
  ];

  // Rates and booking state
  const [rates, setRates] = useState([]);
  const [selectedRate, setSelectedRate] = useState(null);
  const [ratesLoading, setRatesLoading] = useState(false);
  const [bookingLoading, setBookingLoading] = useState(false);
  const [bookingResult, setBookingResult] = useState(null);

  // Error state
  const [error, setError] = useState('');

  // Flight validation state
  const [flightValidated, setFlightValidated] = useState(false);
  const [flightDetails, setFlightDetails] = useState(null);

  // Refs for debouncing
  const pickupSearchTimeout = useRef(null);
  const dropoffSearchTimeout = useRef(null);
  const airlineSearchTimeout = useRef(null);
  const dropoffAirlineSearchTimeout = useRef(null);
  const originAirportSearchTimeout = useRef(null);
  const destinationAirportSearchTimeout = useRef(null);
  const pickupFboAirportTimeout = useRef(null);
  const dropoffFboAirportTimeout = useRef(null);
  const pickupFboTimeout = useRef(null);
  const dropoffFboTimeout = useRef(null);

  // Initialize API and check feature flag on mount
  useEffect(() => {
    initApi();
    checkFeatureFlag();
  }, []);

  const initApi = async () => {
    setConfigLoading(true);
    const result = await initializeUrbanrideApi();
    setApiConfigured(result.configured);
    setApiMode(result.mode);
    setConfigLoading(false);
  };

  const checkFeatureFlag = async () => {
    try {
      const apiBase = getApiBase();
      const headers = getAuthHeaders();
      const response = await axios.get(`${apiBase}/organizations/feature-check/urbanride_quickbook`, { headers });
      setFeatureEnabled(response.data.enabled || false);
    } catch {
      setFeatureEnabled(false);
    }
  };

  // Stop management functions
  const addStop = () => {
    setStops([...stops, {
      type: 'address',
      location: null,
      search: '',
      duration: '',
      searchResults: [],
      showDropdown: false,
      searchLoading: false,
      // FBO-specific fields
      fboAirport: null,
      fboAirportSearch: '',
      fboAirportResults: [],
      showFboAirportDropdown: false,
      fboAirportLoading: false,
      fbo: null,
      fboList: [],
      fboSearch: '',
      showFboDropdown: false,
      fboLoading: false,
      tailNumber: ''
    }]);
  };

  const removeStop = (index) => {
    setStops(stops.filter((_, i) => i !== index));
  };

  const updateStopType = (index, type) => {
    const newStops = [...stops];
    newStops[index] = {
      ...newStops[index],
      type,
      location: null,
      search: '',
      searchResults: [],
      // Reset FBO fields when type changes
      fboAirport: null,
      fboAirportSearch: '',
      fboAirportResults: [],
      showFboAirportDropdown: false,
      fbo: null,
      fboList: [],
      fboSearch: '',
      showFboDropdown: false,
      tailNumber: ''
    };
    setStops(newStops);
  };

  const updateStopDuration = (index, duration) => {
    const newStops = [...stops];
    newStops[index] = { ...newStops[index], duration };
    setStops(newStops);
  };

  const handleStopSearch = useCallback(async (index, query) => {
    // Use functional updates throughout to avoid stale closure issues
    // First, get the stop type before any async operations
    let stopType = 'address';

    setStops(currentStops => {
      const newStops = [...currentStops];
      stopType = newStops[index]?.type || 'address';
      newStops[index] = {
        ...newStops[index],
        search: query,
        location: null
      };
      return newStops;
    });

    if (!query || query.length < 2) {
      setStops(currentStops => {
        const newStops = [...currentStops];
        if (newStops[index]) {
          newStops[index] = {
            ...newStops[index],
            searchResults: [],
            showDropdown: false
          };
        }
        return newStops;
      });
      return;
    }

    // Set loading state
    setStops(currentStops => {
      const newStops = [...currentStops];
      if (newStops[index]) {
        newStops[index] = {
          ...newStops[index],
          searchLoading: true
        };
      }
      return newStops;
    });

    try {
      const locationQuery = { input: query };
      if (stopType === 'airport' || stopType === 'fbo') {
        locationQuery.type = 'airport';
      } else if (stopType === 'train_station') {
        locationQuery.type = 'train';
      }

      const result = await searchLocations(locationQuery);

      // Update with results - use functional update to avoid stale closure
      setStops(currentStops => {
        const updatedStops = [...currentStops];
        if (updatedStops[index]) {
          updatedStops[index] = {
            ...updatedStops[index],
            searchResults: result.locations || [],
            showDropdown: (result.locations || []).length > 0,
            searchLoading: false
          };
        }
        return updatedStops;
      });
    } catch (err) {
      console.error('Stop location search failed:', err);
      // Use functional update to avoid stale closure
      setStops(currentStops => {
        const updatedStops = [...currentStops];
        if (updatedStops[index]) {
          updatedStops[index] = {
            ...updatedStops[index],
            searchResults: [],
            searchLoading: false
          };
        }
        return updatedStops;
      });
    }
  }, []);

  const selectStopLocation = (index, location) => {
    const newStops = [...stops];
    newStops[index] = {
      ...newStops[index],
      location,
      search: location.name,
      searchResults: [],
      showDropdown: false
    };
    setStops(newStops);
  };

  const setStopDropdownVisible = (index, visible) => {
    const newStops = [...stops];
    if (newStops[index]) {
      newStops[index] = { ...newStops[index], showDropdown: visible };
      setStops(newStops);
    }
  };

  // Stop FBO airport search
  const handleStopFboAirportSearch = useCallback(async (index, query) => {
    setStops(currentStops => {
      const newStops = [...currentStops];
      if (newStops[index]) {
        newStops[index] = {
          ...newStops[index],
          fboAirportSearch: query,
          fboAirport: null,
          fboList: [],
          fbo: null,
          fboSearch: ''
        };
      }
      return newStops;
    });

    if (!query || query.length < 2) {
      setStops(currentStops => {
        const newStops = [...currentStops];
        if (newStops[index]) {
          newStops[index] = {
            ...newStops[index],
            fboAirportResults: [],
            showFboAirportDropdown: false
          };
        }
        return newStops;
      });
      return;
    }

    setStops(currentStops => {
      const newStops = [...currentStops];
      if (newStops[index]) {
        newStops[index] = { ...newStops[index], fboAirportLoading: true };
      }
      return newStops;
    });

    try {
      const result = await searchLocations({ input: query, type: 'airport' });
      setStops(currentStops => {
        const newStops = [...currentStops];
        if (newStops[index]) {
          newStops[index] = {
            ...newStops[index],
            fboAirportResults: result.locations || [],
            showFboAirportDropdown: (result.locations || []).length > 0,
            fboAirportLoading: false
          };
        }
        return newStops;
      });
    } catch (err) {
      console.error('Stop FBO airport search failed:', err);
      setStops(currentStops => {
        const newStops = [...currentStops];
        if (newStops[index]) {
          newStops[index] = {
            ...newStops[index],
            fboAirportResults: [],
            fboAirportLoading: false
          };
        }
        return newStops;
      });
    }
  }, []);

  // Select airport for stop FBO
  const selectStopFboAirport = useCallback(async (index, location) => {
    // Extract airport code from location (use 3-letter IATA code for Urbanride API)
    const airportCode = location.id || location.name.match(/\(([A-Z]{3,4})\)/)?.[1] || '';

    setStops(currentStops => {
      const newStops = [...currentStops];
      if (newStops[index]) {
        newStops[index] = {
          ...newStops[index],
          fboAirport: { ...location, icao: airportCode },
          fboAirportSearch: location.name,
          showFboAirportDropdown: false,
          fboLoading: true
        };
      }
      return newStops;
    });

    // Load FBOs for this airport using Urbanride API
    if (airportCode && airportCode.length >= 3) {
      try {

        const apiBase = getApiBase();
        const response = await fetch(
          `${apiBase}/urbanride/location/search`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              input: airportCode,
              type: 'fbo'
            })
          }
        );
        if (response.ok) {
          const data = await response.json();
          // Map Urbanride location response to FBO format
          const fboList = (data.locations || []).map(loc => ({
            id: loc.id,
            name: loc.name,
            address: loc.details,
            airport_icao: airportCode
          }));
          setStops(currentStops => {
            const newStops = [...currentStops];
            if (newStops[index]) {
              newStops[index] = {
                ...newStops[index],
                fboList: fboList,
                fboLoading: false
              };
            }
            return newStops;
          });
        } else {
          setStops(currentStops => {
            const newStops = [...currentStops];
            if (newStops[index]) {
              newStops[index] = { ...newStops[index], fboLoading: false };
            }
            return newStops;
          });
        }
      } catch (err) {
        console.error('Failed to load FBOs for stop:', err);
        setStops(currentStops => {
          const newStops = [...currentStops];
          if (newStops[index]) {
            newStops[index] = { ...newStops[index], fboLoading: false };
          }
          return newStops;
        });
      }
    }
  }, []);

  // Search within FBOs at selected airport for a stop
  const handleStopFboSearch = useCallback(async (index, query) => {
    setStops(currentStops => {
      const newStops = [...currentStops];
      if (newStops[index]) {
        newStops[index] = { ...newStops[index], fboSearch: query };
      }
      return newStops;
    });

    // Get airport ICAO from current stop
    const stop = stops[index];
    const icaoCode = stop?.fboAirport?.icao || stop?.fboAirport?.id;
    if (!icaoCode) return;

    setStops(currentStops => {
      const newStops = [...currentStops];
      if (newStops[index]) {
        newStops[index] = { ...newStops[index], fboLoading: true };
      }
      return newStops;
    });

    try {

      const apiBase = getApiBase();
      // Use Urbanride location/search API with type='fbo'
      const searchInput = query && query.trim().length >= 2
        ? `${icaoCode} ${query.trim()}`
        : icaoCode;
      const response = await fetch(`${apiBase}/urbanride/location/search`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          input: searchInput,
          type: 'fbo'
        })
      });
      if (response.ok) {
        const data = await response.json();
        // Map Urbanride location response to FBO format
        const fboList = (data.locations || []).map(loc => ({
          id: loc.id,
          name: loc.name,
          address: loc.details,
          airport_icao: icaoCode
        }));
        setStops(currentStops => {
          const newStops = [...currentStops];
          if (newStops[index]) {
            newStops[index] = {
              ...newStops[index],
              fboList: fboList,
              fboLoading: false
            };
          }
          return newStops;
        });
      }
    } catch (err) {
      console.error('Stop FBO search failed:', err);
      setStops(currentStops => {
        const newStops = [...currentStops];
        if (newStops[index]) {
          newStops[index] = { ...newStops[index], fboLoading: false };
        }
        return newStops;
      });
    }
  }, [stops]);

  // Select FBO for a stop
  const selectStopFbo = useCallback((index, fbo) => {
    setStops(currentStops => {
      const newStops = [...currentStops];
      if (newStops[index]) {
        newStops[index] = {
          ...newStops[index],
          fbo,
          fboSearch: `${fbo.name} / ${fbo.airport_icao}`,
          showFboDropdown: false,
          // Also set location for consistency
          location: {
            name: `${fbo.name} / ${fbo.airport_icao}`,
            details: fbo.address || '',
            type: 'fbo',
            id: fbo.id,
            fboData: fbo
          }
        };
      }
      return newStops;
    });
  }, []);

  // Update stop tail number
  const updateStopTailNumber = useCallback((index, tailNumber) => {
    setStops(currentStops => {
      const newStops = [...currentStops];
      if (newStops[index]) {
        newStops[index] = { ...newStops[index], tailNumber: tailNumber.toUpperCase() };
      }
      return newStops;
    });
  }, []);

  // Set stop FBO dropdown visibility
  const setStopFboDropdownVisible = useCallback((index, visible) => {
    setStops(currentStops => {
      const newStops = [...currentStops];
      if (newStops[index]) {
        newStops[index] = { ...newStops[index], showFboDropdown: visible };
      }
      return newStops;
    });
  }, []);

  // Search locations with debounce
  const handlePickupSearch = useCallback(async (query) => {
    setPickupSearch(query);
    setPickupLocation(null);

    if (pickupSearchTimeout.current) {
      clearTimeout(pickupSearchTimeout.current);
    }

    if (!query || query.length < 2) {
      setPickupSearchResults([]);
      setShowPickupDropdown(false);
      return;
    }

    pickupSearchTimeout.current = setTimeout(async () => {
      try {
        setPickupSearchLoading(true);
        const locationQuery = { input: query };
        if (pickupType === 'airport') {
          locationQuery.type = 'airport';
        }
        const result = await searchLocations(locationQuery);
        setPickupSearchResults(result.locations || []);
        setShowPickupDropdown(true);
      } catch (err) {
        console.error('Location search failed:', err);
        setPickupSearchResults([]);
      } finally {
        setPickupSearchLoading(false);
      }
    }, 300);
  }, [pickupType]);

  const handleDropoffSearch = useCallback(async (query) => {
    setDropoffSearch(query);
    setDropoffLocation(null);

    if (dropoffSearchTimeout.current) {
      clearTimeout(dropoffSearchTimeout.current);
    }

    if (!query || query.length < 2) {
      setDropoffSearchResults([]);
      setShowDropoffDropdown(false);
      return;
    }

    dropoffSearchTimeout.current = setTimeout(async () => {
      try {
        setDropoffSearchLoading(true);
        const locationQuery = { input: query };
        if (dropoffType === 'airport') {
          locationQuery.type = 'airport';
        }
        const result = await searchLocations(locationQuery);
        setDropoffSearchResults(result.locations || []);
        setShowDropoffDropdown(true);
      } catch (err) {
        console.error('Location search failed:', err);
        setDropoffSearchResults([]);
      } finally {
        setDropoffSearchLoading(false);
      }
    }, 300);
  }, [dropoffType]);

  // Airline search
  const handleAirlineSearch = useCallback(async (query, isDropoff = false) => {
    const setAirline = isDropoff ? setDropoffAirline : setPickupAirline;
    const setResults = isDropoff ? setDropoffAirlineSearchResults : setAirlineSearchResults;
    const setShow = isDropoff ? setShowDropoffAirlineDropdown : setShowAirlineDropdown;
    const setLoading = isDropoff ? setDropoffAirlineSearchLoading : setAirlineSearchLoading;
    const timeout = isDropoff ? dropoffAirlineSearchTimeout : airlineSearchTimeout;

    setAirline(query);

    if (timeout.current) {
      clearTimeout(timeout.current);
    }

    if (!query || query.length < 1) {
      setResults([]);
      setShow(false);
      return;
    }

    timeout.current = setTimeout(async () => {
      try {
        setLoading(true);
        const result = await searchAirlines({ input: query });
        setResults(result.airlines || []);
        setShow(true);
      } catch (err) {
        console.error('Airline search failed:', err);
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 200);
  }, []);

  // Origin/Destination airport search (for "other airport" fields)
  const handleOriginAirportSearch = useCallback(async (query) => {
    setPickupOriginAirport(query);
    setPickupOriginAirportCode('');

    if (originAirportSearchTimeout.current) {
      clearTimeout(originAirportSearchTimeout.current);
    }

    if (!query || query.length < 2) {
      setOriginAirportSearchResults([]);
      setShowOriginAirportDropdown(false);
      return;
    }

    originAirportSearchTimeout.current = setTimeout(async () => {
      try {
        setOriginAirportSearchLoading(true);
        const result = await searchLocations({ input: query, type: 'airport' });
        setOriginAirportSearchResults(result.locations || []);
        setShowOriginAirportDropdown(true);
      } catch (err) {
        console.error('Origin airport search failed:', err);
        setOriginAirportSearchResults([]);
      } finally {
        setOriginAirportSearchLoading(false);
      }
    }, 300);
  }, []);

  const handleDestinationAirportSearch = useCallback(async (query) => {
    setDropoffDestinationAirport(query);
    setDropoffDestinationAirportCode('');

    if (destinationAirportSearchTimeout.current) {
      clearTimeout(destinationAirportSearchTimeout.current);
    }

    if (!query || query.length < 2) {
      setDestinationAirportSearchResults([]);
      setShowDestinationAirportDropdown(false);
      return;
    }

    destinationAirportSearchTimeout.current = setTimeout(async () => {
      try {
        setDestinationAirportSearchLoading(true);
        const result = await searchLocations({ input: query, type: 'airport' });
        setDestinationAirportSearchResults(result.locations || []);
        setShowDestinationAirportDropdown(true);
      } catch (err) {
        console.error('Destination airport search failed:', err);
        setDestinationAirportSearchResults([]);
      } finally {
        setDestinationAirportSearchLoading(false);
      }
    }, 300);
  }, []);

  // Select origin/destination airport handlers
  const selectOriginAirport = (location) => {
    // Extract airport code from location id or name
    const airportCode = location.id || location.name.match(/\(([A-Z]{3})\)/)?.[1] || '';
    setPickupOriginAirport(location.name);
    setPickupOriginAirportCode(airportCode);
    setShowOriginAirportDropdown(false);
  };

  const selectDestinationAirport = (location) => {
    // Extract airport code from location id or name
    const airportCode = location.id || location.name.match(/\(([A-Z]{3})\)/)?.[1] || '';
    setDropoffDestinationAirport(location.name);
    setDropoffDestinationAirportCode(airportCode);
    setShowDestinationAirportDropdown(false);
  };

  // Select location handlers
  const selectPickupLocation = (location) => {
    setPickupLocation(location);
    setPickupSearch(location.name);
    setShowPickupDropdown(false);
  };

  const selectDropoffLocation = (location) => {
    setDropoffLocation(location);
    setDropoffSearch(location.name);
    setShowDropoffDropdown(false);
  };

  // FBO Airport search (for selecting airport where FBO is located)
  const handleFboAirportSearch = useCallback(async (query, isDropoff = false) => {
    const setSearch = isDropoff ? setDropoffFboAirportSearch : setPickupFboAirportSearch;
    const setResults = isDropoff ? setDropoffFboAirportResults : setPickupFboAirportResults;
    const setShow = isDropoff ? setShowDropoffFboAirportDropdown : setShowPickupFboAirportDropdown;
    const setLoading = isDropoff ? setDropoffFboAirportLoading : setPickupFboAirportLoading;
    const timeout = isDropoff ? dropoffFboAirportTimeout : pickupFboAirportTimeout;
    const setAirport = isDropoff ? setDropoffFboAirport : setPickupFboAirport;
    const setFboList = isDropoff ? setDropoffFboList : setPickupFboList;
    const setFbo = isDropoff ? setDropoffFbo : setPickupFbo;
    const setFboSearch = isDropoff ? setDropoffFboSearch : setPickupFboSearch;

    setSearch(query);
    setAirport(null);
    setFboList([]);
    setFbo(null);
    setFboSearch('');

    if (timeout.current) {
      clearTimeout(timeout.current);
    }

    if (!query || query.length < 2) {
      setResults([]);
      setShow(false);
      return;
    }

    timeout.current = setTimeout(async () => {
      try {
        setLoading(true);
        const result = await searchLocations({ input: query, type: 'airport' });
        setResults(result.locations || []);
        setShow(true);
      } catch (err) {
        console.error('FBO airport search failed:', err);
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 300);
  }, []);

  // Select airport for FBO
  const selectFboAirport = useCallback(async (location, isDropoff = false) => {
    const setAirport = isDropoff ? setDropoffFboAirport : setPickupFboAirport;
    const setSearch = isDropoff ? setDropoffFboAirportSearch : setPickupFboAirportSearch;
    const setShow = isDropoff ? setShowDropoffFboAirportDropdown : setShowPickupFboAirportDropdown;
    const setFboList = isDropoff ? setDropoffFboList : setPickupFboList;
    const setFboLoading = isDropoff ? setDropoffFboLoading : setPickupFboLoading;

    // Extract airport code from location (use 3-letter IATA code for Urbanride API)
    const airportCode = location.id || location.name.match(/\(([A-Z]{3,4})\)/)?.[1] || '';

    setAirport({ ...location, icao: airportCode });
    setSearch(location.name);
    setShow(false);

    // Load FBOs for this airport using Urbanride API
    if (airportCode && airportCode.length >= 3) {
      try {
        setFboLoading(true);

        const apiBase = getApiBase();
        const response = await fetch(
          `${apiBase}/urbanride/location/search`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              input: airportCode,
              type: 'fbo'
            })
          }
        );
        if (response.ok) {
          const data = await response.json();
          // Map Urbanride location response to FBO format
          const fboList = (data.locations || []).map(loc => ({
            id: loc.id,
            name: loc.name,
            address: loc.details,
            airport_icao: airportCode
          }));
          setFboList(fboList);
        }
      } catch (err) {
        console.error('Failed to load FBOs:', err);
      } finally {
        setFboLoading(false);
      }
    }
  }, []);

  // Search within FBOs at selected airport
  const handleFboSearch = useCallback(async (query, isDropoff = false) => {
    const setSearch = isDropoff ? setDropoffFboSearch : setPickupFboSearch;
    const setFboList = isDropoff ? setDropoffFboList : setPickupFboList;
    const setFboLoading = isDropoff ? setDropoffFboLoading : setPickupFboLoading;
    const airport = isDropoff ? dropoffFboAirport : pickupFboAirport;
    const timeout = isDropoff ? dropoffFboTimeout : pickupFboTimeout;

    setSearch(query);

    if (timeout.current) {
      clearTimeout(timeout.current);
    }

    const icaoCode = airport?.icao || airport?.id;
    if (!icaoCode) return;

    timeout.current = setTimeout(async () => {
      try {
        setFboLoading(true);

        const apiBase = getApiBase();
        // Use Urbanride location/search API with type='fbo'
        const searchInput = query && query.trim().length >= 2
          ? `${icaoCode} ${query.trim()}`
          : icaoCode;
        const response = await fetch(`${apiBase}/urbanride/location/search`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            input: searchInput,
            type: 'fbo'
          })
        });
        if (response.ok) {
          const data = await response.json();
          // Map Urbanride location response to FBO format
          const fboList = (data.locations || []).map(loc => ({
            id: loc.id,
            name: loc.name,
            address: loc.details,
            airport_icao: icaoCode
          }));
          setFboList(fboList);
        }
      } catch (err) {
        console.error('FBO search failed:', err);
      } finally {
        setFboLoading(false);
      }
    }, 300);
  }, [pickupFboAirport, dropoffFboAirport]);

  // Select FBO
  const selectFbo = useCallback((fbo, isDropoff = false) => {
    const setFbo = isDropoff ? setDropoffFbo : setPickupFbo;
    const setSearch = isDropoff ? setDropoffFboSearch : setPickupFboSearch;
    const setShow = isDropoff ? setShowDropoffFboDropdown : setShowPickupFboDropdown;

    setFbo(fbo);
    setSearch(`${fbo.name} / ${fbo.airport_icao}`);
    setShow(false);
  }, []);

  // Reset FBO state when changing pickup/dropoff type
  const resetPickupFboState = useCallback(() => {
    setPickupFboAirport(null);
    setPickupFboAirportSearch('');
    setPickupFboAirportResults([]);
    setShowPickupFboAirportDropdown(false);
    setPickupFbo(null);
    setPickupFboList([]);
    setPickupFboSearch('');
    setShowPickupFboDropdown(false);
    setPickupTailNumber('');
    setPickupFboDateTime('');
  }, []);

  const resetDropoffFboState = useCallback(() => {
    setDropoffFboAirport(null);
    setDropoffFboAirportSearch('');
    setDropoffFboAirportResults([]);
    setShowDropoffFboAirportDropdown(false);
    setDropoffFbo(null);
    setDropoffFboList([]);
    setDropoffFboSearch('');
    setShowDropoffFboDropdown(false);
    setDropoffTailNumber('');
    setDropoffFboDateTime('');
  }, []);

  const selectAirline = (airline, isDropoff = false) => {
    if (isDropoff) {
      setDropoffAirline(airline.name);
      setDropoffAirlineCode(airline.code);
      setShowDropoffAirlineDropdown(false);
    } else {
      setPickupAirline(airline.name);
      setPickupAirlineCode(airline.code);
      setShowAirlineDropdown(false);
    }
  };

  // Validate flight
  const handleValidateFlight = async (isDropoff = false) => {
    const airport = isDropoff
      ? (dropoffLocation?.id || dropoffSearch.toUpperCase().substring(0, 3))
      : (pickupLocation?.id || pickupSearch.toUpperCase().substring(0, 3));
    const airline = isDropoff ? dropoffAirlineCode : pickupAirlineCode;
    const flightNumber = isDropoff ? dropoffFlightNumber : pickupFlightNumber;
    const date = isDropoff ? dropoffDate : pickupDate;
    const time = isDropoff ? dropoffTime : pickupTime;

    // For airport pickups (not dropoffs), time is optional
    if (isDropoff) {
      if (!airport || !airline || !flightNumber || !date || !time) {
        setError('Please fill in all flight details before validating');
        return;
      }
    } else {
      if (!airport || !airline || !flightNumber || !date) {
        setError('Please fill in airport, airline, flight number, and date before validating');
        return;
      }
    }

    // Use a default time if not provided (noon) for validation purposes
    const dateTime = time ? `${date}T${time}` : `${date}T12:00`;

    try {
      setError('');
      const result = await validateFlight({
        airport: airport.toUpperCase(),
        airline: airline.toUpperCase(),
        flightNumber,
        dateTime,
        departure: isDropoff // true for departure, false for arrival
      });

      // Extract the flight data from the nested response
      const flightData = result?.flight || result;

      // Check for scheduled time in various possible field names
      // The API may return time in different fields depending on the flight type
      // For arrivals, prefer arrivalTimeScheduled, for departures prefer departureTimeScheduled
      const verifiedTime = isDropoff
        ? (flightData?.departureTimeScheduled || flightData?.departureTime || flightData?.scheduledTime || flightData?.time)
        : (flightData?.arrivalTimeScheduled || flightData?.arrivalTime || flightData?.scheduledTime || flightData?.time);

      // If flight is validated and has scheduled time, update the time field
      if (result && verifiedTime) {
        const scheduledDateTime = new Date(verifiedTime);
        const hours = String(scheduledDateTime.getHours()).padStart(2, '0');
        const minutes = String(scheduledDateTime.getMinutes()).padStart(2, '0');
        const exactTime = `${hours}:${minutes}`;

        // Check if user had entered a time that differs from the scheduled time
        const userEnteredTime = isDropoff ? dropoffTime : pickupTime;
        let warningMessage = '';

        if (userEnteredTime && userEnteredTime !== exactTime) {
          warningMessage = ` Note: Your entered time (${userEnteredTime}) was different from the scheduled time and has been updated to ${exactTime}.`;
        }

        if (isDropoff) {
          setDropoffTime(exactTime);
          // Auto-populate destination airport from flight data (for departures)
          if (flightData?.destinationAirport && !dropoffDestinationAirportCode) {
            setDropoffDestinationAirportCode(flightData.destinationAirport);
            setDropoffDestinationAirport(flightData.destinationAirport);
          }
        } else {
          setPickupTime(exactTime);
          // Auto-populate origin airport from flight data (for arrivals)
          if (flightData?.departureAirport && !pickupOriginAirportCode) {
            setPickupOriginAirportCode(flightData.departureAirport);
            setPickupOriginAirport(flightData.departureAirport);
          }
        }

        setFlightDetails(result);
        setFlightValidated(true);
        setError(`Flight validated successfully! ${isDropoff ? 'Departure' : 'Arrival'} time updated to ${exactTime}.${warningMessage}`);
      } else {
        // Even without time, try to auto-populate the origin/destination airport
        if (isDropoff) {
          if (flightData?.destinationAirport && !dropoffDestinationAirportCode) {
            setDropoffDestinationAirportCode(flightData.destinationAirport);
            setDropoffDestinationAirport(flightData.destinationAirport);
          }
        } else {
          if (flightData?.departureAirport && !pickupOriginAirportCode) {
            setPickupOriginAirportCode(flightData.departureAirport);
            setPickupOriginAirport(flightData.departureAirport);
          }
        }

        setFlightDetails(result);
        setFlightValidated(true);
        setError('Flight validated successfully!');
      }

      // Clear success message after 5 seconds
      setTimeout(() => {
        if (error.includes('validated successfully')) {
          setError('');
        }
      }, 5000);
    } catch (err) {
      setError(`Flight validation failed: ${err.message}`);
      setFlightValidated(false);
    }
  };

  // Build booking command
  const buildBookingCmd = () => {
    const pickupDateTime = pickupType === 'airport'
      ? `${pickupDate}T${pickupTime}`
      : pickupAddressDateTime;

    const dropoffDateTime = dropoffType === 'airport'
      ? `${dropoffDate}T${dropoffTime}`
      : (pickupAddressDateTime || `${pickupDate}T${pickupTime}`);

    const pickup = pickupType === 'airport'
      ? createAirportLocation({
          airport: pickupLocation?.id || pickupSearch.toUpperCase().substring(0, 3),
          airline: pickupAirlineCode,
          flightNumber: pickupFlightNumber,
          dateTime: pickupDateTime,
          departure: false
        })
      : createAddressLocation({
          id: pickupLocation?.id || '',
          placeId: pickupLocation?.placeId || pickupLocation?.id || '',  // Use placeId from search if available, fallback to id
          dateTime: pickupDateTime
        });

    const dropoff = dropoffType === 'as_directed'
      ? createAsDirectedLocation({
          dateTime: dropoffDateTime,
          comments: 'Passenger/s will direct to final destination.'
        })
      : dropoffType === 'airport'
        ? createAirportLocation({
            airport: dropoffLocation?.id || dropoffSearch.toUpperCase().substring(0, 3),
            airline: dropoffAirlineCode,
            flightNumber: dropoffFlightNumber,
            dateTime: dropoffDateTime,
            departure: true
          })
        : createAddressLocation({
            id: dropoffLocation?.id || '',
            placeId: dropoffLocation?.placeId || dropoffLocation?.id || '',  // Use placeId from search if available, fallback to id
            dateTime: dropoffDateTime
          });

    // Build stops array for the API
    const stopsData = stops
      .filter(stop => stop.type === 'as_directed' || stop.location) // Include as_directed stops or stops with selected locations
      .map(stop => {
        // For "as_directed" stops, use the special location type
        if (stop.type === 'as_directed') {
          return createAsDirectedLocation({
            dateTime: pickupDateTime
          });
        }
        // For other stop types, use address location
        return createAddressLocation({
          id: stop.location?.id || '',
          placeId: stop.location?.placeId || stop.location?.id || '',
          dateTime: pickupDateTime, // Stops use the same base datetime
          comments: stop.duration ? `Wait time: ${stop.duration} minutes` : ''
        });
      });

    return {
      pickup,
      dropoff,
      stops: stopsData,
      passengers: [createPassenger(passenger)],
      clientComments,
      asQuote: false
    };
  };

  // Get rates
  const handleGetRates = async () => {
    setError('');

    // Validate required fields
    if (!pickupLocation && !pickupSearch) {
      setError('Please select a pickup location');
      return;
    }
    if (dropoffType !== 'as_directed' && !dropoffLocation && !dropoffSearch) {
      setError('Please select a dropoff location');
      return;
    }
    if (pickupType === 'airport' && (!pickupAirlineCode || !pickupFlightNumber || !pickupDate)) {
      setError('Please fill in pickup flight details (airport, airline, flight number, and date)');
      return;
    }
    if (pickupType === 'airport' && !pickupTime) {
      setError('Please validate the flight to get the arrival time, or enter an arrival time manually');
      return;
    }
    if (dropoffType === 'airport' && (!dropoffAirlineCode || !dropoffFlightNumber || !dropoffDate || !dropoffTime)) {
      setError('Please fill in all dropoff flight details');
      return;
    }
    if (!pickupAddressDateTime && pickupType !== 'airport') {
      setError('Please select a pickup date and time');
      return;
    }
    if (!passenger.firstName || !passenger.lastName || !passenger.email || !passenger.mobile) {
      setError('Please fill in all passenger details');
      return;
    }

    try {
      setRatesLoading(true);
      const bookingCmd = buildBookingCmd();

      const result = await getRates(bookingCmd);
      setRates(result.rates || []);
      setStep(2);
    } catch (err) {
      console.error('Get Rates Error:', err);
      console.error('Error response:', err.response?.data);
      setError(`Failed to get rates: ${err.message}`);
    } finally {
      setRatesLoading(false);
    }
  };

  // Book trip
  const handleBookTrip = async () => {
    if (!selectedRate) {
      setError('Please select a vehicle');
      return;
    }

    try {
      setError('');
      setBookingLoading(true);
      const bookingCmd = buildBookingCmd();
      bookingCmd.vehicleId = selectedRate.vehicleId;
      bookingCmd.confirmationId = selectedRate.confirmationId;

      const result = await bookTrip(bookingCmd);
      setBookingResult(result);
      setStep(3);
    } catch (err) {
      setError(`Booking failed: ${err.message}`);
    } finally {
      setBookingLoading(false);
    }
  };

  // Reset form
  const handleNewBooking = () => {
    setStep(1);
    setPickupLocation(null);
    setPickupSearch('');
    setDropoffLocation(null);
    setDropoffSearch('');
    setPassenger({ title: '', firstName: '', lastName: '', email: '', mobile: '', vip: false });
    setClientComments('');
    setRates([]);
    setSelectedRate(null);
    setBookingResult(null);
    setFlightValidated(false);
    setFlightDetails(null);
    setPickupAirline('');
    setPickupAirlineCode('');
    setPickupFlightNumber('');
    setPickupDate('');
    setPickupTime('');
    setPickupAddressDateTime('');
    setPickupOriginAirport('');
    setPickupOriginAirportCode('');
    setDropoffAirline('');
    setDropoffAirlineCode('');
    setDropoffFlightNumber('');
    setDropoffDate('');
    setDropoffTime('');
    setDropoffDestinationAirport('');
    setDropoffDestinationAirportCode('');
  };

  // Loading state
  if (configLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  // Not configured state
  if (!apiConfigured) {
    return (
      <div className="max-w-2xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-semibold text-slate-800">Chauffeur Transfer Booking</h2>
          <Link to="/" className="px-3 py-2 text-sm border border-slate-300 rounded-md hover:bg-slate-50">
            Dashboard
          </Link>
        </div>

        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <div className="text-center py-8">
            <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-slate-800 mb-2">Urbanride API Not Configured</h3>
            <p className="text-slate-600 mb-4">
              To book chauffeur services, you need to configure your Urbanride API keys in your profile settings.
            </p>
            <Link
              to="/profile"
              className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              Go to Profile Settings
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Block access if feature is disabled
  if (featureEnabled === false) {
    return (
      <div className="max-w-4xl mx-auto py-12 text-center">
        <h2 className="text-2xl font-semibold text-slate-800 mb-4">Chauffeur Service Not Available</h2>
        <p className="text-slate-600 mb-6">
          The Urbanride chauffeur service feature is not enabled for your organization.
          Please contact your administrator to enable it.
        </p>
        <Link to="/trips" className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700">
          Back to Trips
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-semibold text-slate-800">Chauffeur Transfer Booking</h2>
          <div className="flex items-center gap-2 mt-1">
            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${
              apiMode === 'production' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
            }`}>
              <span className={`w-2 h-2 rounded-full ${apiMode === 'production' ? 'bg-green-500' : 'bg-yellow-500'}`}></span>
              {apiMode === 'production' ? 'Production Mode' : 'Test Mode'}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Link to="/" className="px-3 py-2 text-sm border border-slate-300 rounded-md hover:bg-slate-50">
            Dashboard
          </Link>
          <button
            onClick={() => navigate(-1)}
            className="px-3 py-2 text-sm border border-slate-300 rounded-md hover:bg-slate-50"
          >
            Back
          </button>
        </div>
      </div>

      {/* Progress Steps */}
      <div className="mb-6">
        <div className="flex items-center">
          <div className={`flex items-center justify-center w-8 h-8 rounded-full ${step >= 1 ? 'bg-blue-600 text-white' : 'bg-slate-200 text-slate-500'}`}>1</div>
          <div className={`flex-1 h-1 mx-2 ${step >= 2 ? 'bg-blue-600' : 'bg-slate-200'}`}></div>
          <div className={`flex items-center justify-center w-8 h-8 rounded-full ${step >= 2 ? 'bg-blue-600 text-white' : 'bg-slate-200 text-slate-500'}`}>2</div>
          <div className={`flex-1 h-1 mx-2 ${step >= 3 ? 'bg-blue-600' : 'bg-slate-200'}`}></div>
          <div className={`flex items-center justify-center w-8 h-8 rounded-full ${step >= 3 ? 'bg-blue-600 text-white' : 'bg-slate-200 text-slate-500'}`}>3</div>
        </div>
        <div className="flex justify-between mt-1 text-xs text-slate-500">
          <span>Trip Details</span>
          <span>Select Vehicle</span>
          <span>Confirmation</span>
        </div>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-md">
          {error}
        </div>
      )}

      {/* Step 1: Form */}
      {step === 1 && (
        <div className="bg-white rounded-xl border border-slate-200 p-6 space-y-6">
          {/* Pickup Section */}
          <div>
            <h3 className="text-lg font-semibold text-slate-800 mb-4">Pickup</h3>

            <div className="mb-4">
              <div className="flex items-center gap-4 mb-3 flex-wrap">
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="pickupType"
                    checked={pickupType === 'airport'}
                    onChange={() => { setPickupType('airport'); setPickupLocation(null); setPickupSearch(''); resetPickupFboState(); }}
                  />
                  <span>Airport</span>
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="pickupType"
                    checked={pickupType === 'address'}
                    onChange={() => { setPickupType('address'); setPickupLocation(null); setPickupSearch(''); resetPickupFboState(); }}
                  />
                  <span>Address</span>
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="pickupType"
                    checked={pickupType === 'fbo'}
                    onChange={() => { setPickupType('fbo'); setPickupLocation(null); setPickupSearch(''); resetPickupFboState(); }}
                  />
                  <span>FBO (Private Aviation)</span>
                </label>
              </div>

              {/* FBO Selection UI */}
              {pickupType === 'fbo' && (
                <div className="space-y-4 mb-4 p-4 bg-slate-50 rounded-lg border border-slate-200">
                  {/* Step 1: Airport Selection */}
                  <div className="relative">
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      Step 1: Select Airport
                    </label>
                    <div className="relative">
                      <input
                        type="text"
                        value={pickupFboAirportSearch}
                        onChange={(e) => handleFboAirportSearch(e.target.value, false)}
                        onFocus={() => pickupFboAirportResults.length > 0 && setShowPickupFboAirportDropdown(true)}
                        placeholder="Search airport by code or name (e.g., KTEB, Teterboro)"
                        className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm"
                      />
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <Plane className="h-4 w-4 text-slate-400" />
                      </div>
                      {pickupFboAirportLoading && (
                        <div className="absolute right-3 top-1/2 -translate-y-1/2">
                          <div className="animate-spin h-4 w-4 border-2 border-purple-600 rounded-full border-t-transparent"></div>
                        </div>
                      )}
                    </div>
                    {showPickupFboAirportDropdown && pickupFboAirportResults.length > 0 && (
                      <div className="absolute z-20 w-full mt-1 bg-white border border-slate-200 rounded-md shadow-lg max-h-48 overflow-auto">
                        {pickupFboAirportResults.map((loc, idx) => (
                          <button
                            key={idx}
                            type="button"
                            className="w-full px-3 py-2 text-left hover:bg-slate-50 border-b border-slate-100 last:border-0"
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => selectFboAirport(loc, false)}
                          >
                            <div className="font-medium text-slate-800 text-sm">{loc.name}</div>
                            {loc.details && <div className="text-xs text-slate-500">{loc.details}</div>}
                          </button>
                        ))}
                      </div>
                    )}
                    {pickupFboAirport && (
                      <div className="mt-1 text-xs text-green-600 flex items-center gap-1">
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        Airport selected: {pickupFboAirport.name}
                      </div>
                    )}
                  </div>

                  {/* Step 2: FBO Selection (only shown after airport is selected) */}
                  {pickupFboAirport && (
                    <div className="relative">
                      <label className="block text-sm font-medium text-slate-700 mb-1">
                        Step 2: Select FBO
                      </label>
                      <div className="relative">
                        <input
                          type="text"
                          value={pickupFboSearch}
                          onChange={(e) => handleFboSearch(e.target.value, false)}
                          onFocus={() => {
                            setShowPickupFboDropdown(true);
                            if (pickupFboList.length === 0 && pickupFboAirport) {
                              handleFboSearch('', false);
                            }
                          }}
                          placeholder="Search or select FBO..."
                          className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm"
                        />
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <Building className="h-4 w-4 text-slate-400" />
                        </div>
                        {pickupFboLoading && (
                          <div className="absolute right-3 top-1/2 -translate-y-1/2">
                            <div className="animate-spin h-4 w-4 border-2 border-purple-600 rounded-full border-t-transparent"></div>
                          </div>
                        )}
                      </div>
                      {showPickupFboDropdown && (
                        <div className="absolute z-20 w-full mt-1 bg-white border border-slate-200 rounded-md shadow-lg max-h-64 overflow-auto">
                          {pickupFboLoading && (
                            <div className="px-4 py-3 text-slate-500 text-center text-sm">
                              Loading FBOs...
                            </div>
                          )}
                          {!pickupFboLoading && pickupFboList.length === 0 && (
                            <div className="px-4 py-3 text-slate-500 text-center text-sm">
                              No FBOs found at this airport
                            </div>
                          )}
                          {!pickupFboLoading && pickupFboList.length > 0 && (
                            <>
                              <div className="px-3 py-2 bg-slate-50 border-b border-slate-200">
                                <div className="text-xs font-medium text-slate-600">
                                  {pickupFboList.length} FBO{pickupFboList.length !== 1 ? 's' : ''} at {pickupFboAirport?.icao || pickupFboAirport?.id}
                                </div>
                              </div>
                              {pickupFboList.map((fbo, idx) => (
                                <button
                                  key={fbo.id || idx}
                                  type="button"
                                  className="w-full px-3 py-2 text-left hover:bg-purple-50 border-b border-slate-100 last:border-0"
                                  onMouseDown={(e) => e.preventDefault()}
                                  onClick={() => selectFbo(fbo, false)}
                                >
                                  <div className="font-medium text-slate-800 text-sm">{fbo.name}</div>
                                  {fbo.address && <div className="text-xs text-slate-500">{fbo.address}</div>}
                                  {fbo.phone && <div className="text-xs text-slate-400">📞 {fbo.phone}</div>}
                                </button>
                              ))}
                            </>
                          )}
                        </div>
                      )}
                      {pickupFbo && (
                        <div className="mt-1 text-xs text-green-600 flex items-center gap-1">
                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                          FBO selected: {pickupFbo.name}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Tail Number (Optional) */}
                  {pickupFbo && (
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">
                        Tail Number (Optional)
                      </label>
                      <input
                        type="text"
                        value={pickupTailNumber}
                        onChange={(e) => setPickupTailNumber(e.target.value.toUpperCase())}
                        placeholder="e.g., N12345"
                        className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm"
                      />
                    </div>
                  )}

                  {/* Arrival Date/Time */}
                  {pickupFbo && (
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">
                        Arrival Date & Time
                      </label>
                      <input
                        type="datetime-local"
                        value={pickupFboDateTime}
                        onChange={(e) => setPickupFboDateTime(e.target.value)}
                        className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm"
                      />
                    </div>
                  )}
                </div>
              )}

              {/* Location Search (for airport and address types only) */}
              {pickupType !== 'fbo' && (
                <div className="relative">
                  <input
                    type="text"
                    value={pickupSearch}
                    onChange={(e) => handlePickupSearch(e.target.value)}
                    onFocus={() => pickupSearchResults.length > 0 && setShowPickupDropdown(true)}
                    placeholder={pickupType === 'airport' ? 'Search by airport code or name (e.g., JFK, Los Angeles)' : 'Search address'}
                    className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  {pickupSearchLoading && (
                    <div className="absolute right-3 top-2.5">
                      <div className="animate-spin h-5 w-5 border-2 border-blue-600 rounded-full border-t-transparent"></div>
                    </div>
                  )}
                  {showPickupDropdown && pickupSearchResults.length > 0 && (
                    <div className="absolute z-10 w-full mt-1 bg-white border border-slate-200 rounded-md shadow-lg max-h-60 overflow-auto">
                      {pickupSearchResults.map((loc, idx) => (
                        <button
                          key={idx}
                          className="w-full px-3 py-2 text-left hover:bg-slate-50 border-b border-slate-100 last:border-0"
                          onClick={() => selectPickupLocation(loc)}
                        >
                          <div className="font-medium text-slate-800">{loc.name}</div>
                          <div className="text-sm text-slate-500">{loc.details}</div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Airport-specific fields */}
            {pickupType === 'airport' && (
              <div className="grid md:grid-cols-2 gap-4">
                {/* Airline */}
                <div className="relative">
                  <label className="block text-sm font-medium text-slate-700 mb-1">Airline</label>
                  <input
                    type="text"
                    value={pickupAirline}
                    onChange={(e) => handleAirlineSearch(e.target.value, false)}
                    onFocus={() => airlineSearchResults.length > 0 && setShowAirlineDropdown(true)}
                    placeholder="Search airline"
                    className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  {showAirlineDropdown && airlineSearchResults.length > 0 && (
                    <div className="absolute z-10 w-full mt-1 bg-white border border-slate-200 rounded-md shadow-lg max-h-40 overflow-auto">
                      {airlineSearchResults.map((airline, idx) => (
                        <button
                          key={idx}
                          className="w-full px-3 py-2 text-left hover:bg-slate-50 border-b border-slate-100 last:border-0"
                          onClick={() => selectAirline(airline, false)}
                        >
                          <span className="font-medium">{airline.code}</span> - {airline.name}
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* Flight Number */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Flight Number</label>
                  <input
                    type="text"
                    value={pickupFlightNumber}
                    onChange={(e) => setPickupFlightNumber(e.target.value)}
                    placeholder="e.g., 123"
                    className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                {/* Arrival Date */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Arrival Date</label>
                  <input
                    type="date"
                    value={pickupDate}
                    onChange={(e) => setPickupDate(e.target.value)}
                    className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                {/* Arrival Time */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Arrival Time (optional)</label>
                  <div className="flex gap-2">
                    <input
                      type="time"
                      value={pickupTime}
                      onChange={(e) => setPickupTime(e.target.value)}
                      className="flex-1 px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Auto-filled from validation"
                    />
                    <button
                      type="button"
                      onClick={() => handleValidateFlight(false)}
                      disabled={!pickupAirlineCode || !pickupFlightNumber || !pickupDate}
                      className="px-4 py-2 bg-green-600 text-white text-sm rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                      title="Validate flight and get exact arrival time"
                    >
                      Validate Flight
                    </button>
                  </div>
                </div>

                {/* Origin Airport (optional) */}
                <div className="relative">
                  <label className="block text-sm font-medium text-slate-700 mb-1">Flight Origin (optional)</label>
                  <input
                    type="text"
                    value={pickupOriginAirport}
                    onChange={(e) => handleOriginAirportSearch(e.target.value)}
                    onFocus={() => originAirportSearchResults.length > 0 && setShowOriginAirportDropdown(true)}
                    placeholder="Search airport code or name"
                    className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  {originAirportSearchLoading && (
                    <div className="absolute right-3 top-8">
                      <div className="animate-spin h-5 w-5 border-2 border-blue-600 rounded-full border-t-transparent"></div>
                    </div>
                  )}
                  {showOriginAirportDropdown && originAirportSearchResults.length > 0 && (
                    <div className="absolute z-10 w-full mt-1 bg-white border border-slate-200 rounded-md shadow-lg max-h-40 overflow-auto">
                      {originAirportSearchResults.map((loc, idx) => (
                        <button
                          key={idx}
                          className="w-full px-3 py-2 text-left hover:bg-slate-50 border-b border-slate-100 last:border-0"
                          onClick={() => selectOriginAirport(loc)}
                        >
                          <div className="font-medium text-slate-800">{loc.id && <span className="text-blue-600">{loc.id}</span>} {loc.name}</div>
                          <div className="text-sm text-slate-500">{loc.details}</div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Address-specific fields */}
            {pickupType === 'address' && (
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Pickup Date & Time</label>
                <input
                  type="datetime-local"
                  value={pickupAddressDateTime}
                  onChange={(e) => setPickupAddressDateTime(e.target.value)}
                  className="w-full md:w-1/2 px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            )}
          </div>

          {/* Additional Stops Section */}
          <div className="bg-slate-50 rounded-lg p-4">
            <div className="flex items-center justify-between mb-4">
              <h4 className="text-md font-semibold text-slate-800">Additional Stops (Optional)</h4>
              <button
                type="button"
                onClick={addStop}
                className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-700"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Add Stop
              </button>
            </div>

            {stops.length === 0 ? (
              <p className="text-sm text-slate-500">No additional stops added. Click "Add Stop" to include stops between pickup and dropoff.</p>
            ) : (
              <div className="space-y-4">
                {stops.map((stop, index) => (
                  <div key={index} className="bg-white border border-slate-200 rounded-lg p-3">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-sm font-medium text-slate-700">Stop {index + 1}</span>
                      <button
                        type="button"
                        onClick={() => removeStop(index)}
                        className="text-red-500 hover:text-red-700 p-1"
                        title="Remove stop"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>

                    <div className="grid md:grid-cols-2 gap-3">
                      {/* Stop Type */}
                      <div className={stop.type === 'as_directed' ? 'md:col-span-2' : ''}>
                        <label className="block text-xs font-medium text-slate-700 mb-1">Stop Type</label>
                        <select
                          value={stop.type}
                          onChange={(e) => updateStopType(index, e.target.value)}
                          className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm bg-white"
                        >
                          {STOP_TYPES.map((type) => (
                            <option key={type.value} value={type.value}>{type.label}</option>
                          ))}
                        </select>
                      </div>

                      {/* Approximate Wait Time - Hidden for As Directed */}
                      {stop.type !== 'as_directed' && (
                        <div>
                          <label className="block text-xs font-medium text-slate-700 mb-1">Approximate Wait Time</label>
                          <select
                            value={stop.duration}
                            onChange={(e) => updateStopDuration(index, e.target.value)}
                            className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm bg-white"
                          >
                            {DURATION_OPTIONS.map((opt) => (
                              <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                          </select>
                        </div>
                      )}

                      {/* As Directed Info Message */}
                      {stop.type === 'as_directed' && (
                        <div className="md:col-span-2 bg-blue-50 border border-blue-200 rounded-md p-3">
                          <div className="flex items-start gap-2">
                            <svg className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <div>
                              <p className="text-sm font-medium text-blue-800">Chauffeur Service - As Directed</p>
                              <p className="text-xs text-blue-700 mt-1">Passenger/s will direct the driver to one or more stops during the journey.</p>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* FBO Selection - Two-step flow */}
                      {stop.type === 'fbo' && (
                        <div className="md:col-span-2 space-y-3 p-3 bg-slate-50 rounded-lg border border-slate-200">
                          {/* Step 1: Airport Selection */}
                          <div className="relative">
                            <label className="block text-xs font-medium text-slate-700 mb-1">
                              Step 1: Select Airport
                            </label>
                            <div className="relative">
                              <input
                                type="text"
                                value={stop.fboAirportSearch || ''}
                                onChange={(e) => handleStopFboAirportSearch(index, e.target.value)}
                                onFocus={() => stop.fboAirportResults?.length > 0 && setStops(prev => {
                                  const newStops = [...prev];
                                  newStops[index] = { ...newStops[index], showFboAirportDropdown: true };
                                  return newStops;
                                })}
                                placeholder="Search airport by code or name"
                                className="w-full pl-9 pr-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm"
                              />
                              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                <Plane className="h-4 w-4 text-slate-400" />
                              </div>
                              {stop.fboAirportLoading && (
                                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                                  <div className="animate-spin h-4 w-4 border-2 border-purple-600 rounded-full border-t-transparent"></div>
                                </div>
                              )}
                            </div>
                            {stop.showFboAirportDropdown && stop.fboAirportResults?.length > 0 && (
                              <div className="absolute z-20 w-full mt-1 bg-white border border-slate-200 rounded-md shadow-lg max-h-40 overflow-auto">
                                {stop.fboAirportResults.map((loc, locIdx) => (
                                  <button
                                    key={locIdx}
                                    type="button"
                                    className="w-full px-3 py-2 text-left hover:bg-slate-50 border-b border-slate-100 last:border-0"
                                    onMouseDown={(e) => e.preventDefault()}
                                    onClick={() => selectStopFboAirport(index, loc)}
                                  >
                                    <div className="font-medium text-slate-800 text-sm">{loc.name}</div>
                                    {loc.details && <div className="text-xs text-slate-500">{loc.details}</div>}
                                  </button>
                                ))}
                              </div>
                            )}
                            {stop.fboAirport && (
                              <div className="mt-1 text-xs text-green-600 flex items-center gap-1">
                                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                                {stop.fboAirport.name}
                              </div>
                            )}
                          </div>

                          {/* Step 2: FBO Selection */}
                          {stop.fboAirport && (
                            <div className="relative">
                              <label className="block text-xs font-medium text-slate-700 mb-1">
                                Step 2: Select FBO
                              </label>
                              <div className="relative">
                                <input
                                  type="text"
                                  value={stop.fboSearch || ''}
                                  onChange={(e) => handleStopFboSearch(index, e.target.value)}
                                  onFocus={() => {
                                    setStopFboDropdownVisible(index, true);
                                    if ((stop.fboList || []).length === 0 && stop.fboAirport) {
                                      handleStopFboSearch(index, '');
                                    }
                                  }}
                                  placeholder="Search or select FBO..."
                                  className="w-full pl-9 pr-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm"
                                />
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                  <Building className="h-4 w-4 text-slate-400" />
                                </div>
                                {stop.fboLoading && (
                                  <div className="absolute right-3 top-1/2 -translate-y-1/2">
                                    <div className="animate-spin h-4 w-4 border-2 border-purple-600 rounded-full border-t-transparent"></div>
                                  </div>
                                )}
                              </div>
                              {stop.showFboDropdown && (
                                <div className="absolute z-20 w-full mt-1 bg-white border border-slate-200 rounded-md shadow-lg max-h-48 overflow-auto">
                                  {stop.fboLoading && (
                                    <div className="px-3 py-2 text-slate-500 text-center text-sm">Loading FBOs...</div>
                                  )}
                                  {!stop.fboLoading && (stop.fboList || []).length === 0 && (
                                    <div className="px-3 py-2 text-slate-500 text-center text-sm">No FBOs found</div>
                                  )}
                                  {!stop.fboLoading && (stop.fboList || []).length > 0 && (
                                    <>
                                      <div className="px-3 py-1 bg-slate-50 border-b text-xs text-slate-600">
                                        {stop.fboList.length} FBO{stop.fboList.length !== 1 ? 's' : ''} at {stop.fboAirport?.icao || stop.fboAirport?.id}
                                      </div>
                                      {stop.fboList.map((fbo, fboIdx) => (
                                        <button
                                          key={fbo.id || fboIdx}
                                          type="button"
                                          className="w-full px-3 py-2 text-left hover:bg-purple-50 border-b border-slate-100 last:border-0"
                                          onMouseDown={(e) => e.preventDefault()}
                                          onClick={() => selectStopFbo(index, fbo)}
                                        >
                                          <div className="font-medium text-slate-800 text-sm">{fbo.name}</div>
                                          {fbo.address && <div className="text-xs text-slate-500">{fbo.address}</div>}
                                        </button>
                                      ))}
                                    </>
                                  )}
                                </div>
                              )}
                              {stop.fbo && (
                                <div className="mt-1 text-xs text-green-600 flex items-center gap-1">
                                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                  </svg>
                                  {stop.fbo.name}
                                </div>
                              )}
                            </div>
                          )}

                          {/* Tail Number (Optional) */}
                          {stop.fbo && (
                            <div>
                              <label className="block text-xs font-medium text-slate-700 mb-1">
                                Tail Number (Optional)
                              </label>
                              <input
                                type="text"
                                value={stop.tailNumber || ''}
                                onChange={(e) => updateStopTailNumber(index, e.target.value)}
                                placeholder="e.g., N12345"
                                className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm"
                              />
                            </div>
                          )}
                        </div>
                      )}

                      {/* Location Search - Full Width (Hidden for As Directed and FBO) */}
                      {stop.type !== 'as_directed' && stop.type !== 'fbo' && (
                        <div className="md:col-span-2 relative">
                          <label className="block text-xs font-medium text-slate-700 mb-1">
                            {stop.type === 'airport' ? 'Airport' :
                             stop.type === 'train_station' ? 'Train Station' : 'Address'}
                          </label>
                          <input
                            type="text"
                            value={stop.search}
                            onChange={(e) => handleStopSearch(index, e.target.value)}
                            onFocus={() => stop.searchResults?.length > 0 && setStopDropdownVisible(index, true)}
                            placeholder={
                              stop.type === 'airport' ? 'Search airport (e.g., JFK, LAX)' :
                              stop.type === 'train_station' ? 'Search train station' :
                              'Search address'
                            }
                            className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                          />
                          {stop.searchLoading && (
                            <div className="absolute right-3 top-8">
                              <div className="animate-spin h-4 w-4 border-2 border-blue-600 rounded-full border-t-transparent"></div>
                            </div>
                          )}
                          {stop.showDropdown && stop.searchResults?.length > 0 && (
                            <div className="absolute z-10 w-full mt-1 bg-white border border-slate-200 rounded-md shadow-lg max-h-40 overflow-auto">
                              {stop.searchResults.map((loc, locIdx) => (
                                <button
                                  key={locIdx}
                                  type="button"
                                  className="w-full px-3 py-2 text-left hover:bg-slate-50 border-b border-slate-100 last:border-0"
                                  onMouseDown={(e) => e.preventDefault()}
                                  onClick={() => selectStopLocation(index, loc)}
                                >
                                  <div className="font-medium text-slate-800 text-sm">{loc.name}</div>
                                  {loc.details && <div className="text-xs text-slate-500">{loc.details}</div>}
                                </button>
                              ))}
                            </div>
                          )}
                          {stop.location && (
                            <div className="mt-1 text-xs text-green-600 flex items-center gap-1">
                              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                              </svg>
                              Location selected
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Dropoff Section */}
          <div className="border-t border-slate-200 pt-6">
            <h3 className="text-lg font-semibold text-slate-800 mb-4">Dropoff</h3>

            <div className="mb-4">
              <div className="flex items-center gap-4 mb-3 flex-wrap">
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="dropoffType"
                    checked={dropoffType === 'airport'}
                    onChange={() => { setDropoffType('airport'); setDropoffLocation(null); setDropoffSearch(''); resetDropoffFboState(); }}
                  />
                  <span>Airport</span>
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="dropoffType"
                    checked={dropoffType === 'address'}
                    onChange={() => { setDropoffType('address'); setDropoffLocation(null); setDropoffSearch(''); resetDropoffFboState(); }}
                  />
                  <span>Address</span>
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="dropoffType"
                    checked={dropoffType === 'fbo'}
                    onChange={() => { setDropoffType('fbo'); setDropoffLocation(null); setDropoffSearch(''); resetDropoffFboState(); }}
                  />
                  <span>FBO (Private Aviation)</span>
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="dropoffType"
                    checked={dropoffType === 'as_directed'}
                    onChange={() => { setDropoffType('as_directed'); setDropoffLocation(null); setDropoffSearch(''); resetDropoffFboState(); }}
                  />
                  <span>As Directed</span>
                </label>
              </div>

              {/* FBO Selection UI */}
              {dropoffType === 'fbo' && (
                <div className="space-y-4 mb-4 p-4 bg-slate-50 rounded-lg border border-slate-200">
                  {/* Step 1: Airport Selection */}
                  <div className="relative">
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      Step 1: Select Airport
                    </label>
                    <div className="relative">
                      <input
                        type="text"
                        value={dropoffFboAirportSearch}
                        onChange={(e) => handleFboAirportSearch(e.target.value, true)}
                        onFocus={() => dropoffFboAirportResults.length > 0 && setShowDropoffFboAirportDropdown(true)}
                        placeholder="Search airport by code or name (e.g., KTEB, Teterboro)"
                        className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm"
                      />
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <Plane className="h-4 w-4 text-slate-400" />
                      </div>
                      {dropoffFboAirportLoading && (
                        <div className="absolute right-3 top-1/2 -translate-y-1/2">
                          <div className="animate-spin h-4 w-4 border-2 border-purple-600 rounded-full border-t-transparent"></div>
                        </div>
                      )}
                    </div>
                    {showDropoffFboAirportDropdown && dropoffFboAirportResults.length > 0 && (
                      <div className="absolute z-20 w-full mt-1 bg-white border border-slate-200 rounded-md shadow-lg max-h-48 overflow-auto">
                        {dropoffFboAirportResults.map((loc, idx) => (
                          <button
                            key={idx}
                            type="button"
                            className="w-full px-3 py-2 text-left hover:bg-slate-50 border-b border-slate-100 last:border-0"
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => selectFboAirport(loc, true)}
                          >
                            <div className="font-medium text-slate-800 text-sm">{loc.name}</div>
                            {loc.details && <div className="text-xs text-slate-500">{loc.details}</div>}
                          </button>
                        ))}
                      </div>
                    )}
                    {dropoffFboAirport && (
                      <div className="mt-1 text-xs text-green-600 flex items-center gap-1">
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        Airport selected: {dropoffFboAirport.name}
                      </div>
                    )}
                  </div>

                  {/* Step 2: FBO Selection */}
                  {dropoffFboAirport && (
                    <div className="relative">
                      <label className="block text-sm font-medium text-slate-700 mb-1">
                        Step 2: Select FBO
                      </label>
                      <div className="relative">
                        <input
                          type="text"
                          value={dropoffFboSearch}
                          onChange={(e) => handleFboSearch(e.target.value, true)}
                          onFocus={() => {
                            setShowDropoffFboDropdown(true);
                            if (dropoffFboList.length === 0 && dropoffFboAirport) {
                              handleFboSearch('', true);
                            }
                          }}
                          placeholder="Search or select FBO..."
                          className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm"
                        />
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <Building className="h-4 w-4 text-slate-400" />
                        </div>
                        {dropoffFboLoading && (
                          <div className="absolute right-3 top-1/2 -translate-y-1/2">
                            <div className="animate-spin h-4 w-4 border-2 border-purple-600 rounded-full border-t-transparent"></div>
                          </div>
                        )}
                      </div>
                      {showDropoffFboDropdown && (
                        <div className="absolute z-20 w-full mt-1 bg-white border border-slate-200 rounded-md shadow-lg max-h-64 overflow-auto">
                          {dropoffFboLoading && (
                            <div className="px-4 py-3 text-slate-500 text-center text-sm">
                              Loading FBOs...
                            </div>
                          )}
                          {!dropoffFboLoading && dropoffFboList.length === 0 && (
                            <div className="px-4 py-3 text-slate-500 text-center text-sm">
                              No FBOs found at this airport
                            </div>
                          )}
                          {!dropoffFboLoading && dropoffFboList.length > 0 && (
                            <>
                              <div className="px-3 py-2 bg-slate-50 border-b border-slate-200">
                                <div className="text-xs font-medium text-slate-600">
                                  {dropoffFboList.length} FBO{dropoffFboList.length !== 1 ? 's' : ''} at {dropoffFboAirport?.icao || dropoffFboAirport?.id}
                                </div>
                              </div>
                              {dropoffFboList.map((fbo, idx) => (
                                <button
                                  key={fbo.id || idx}
                                  type="button"
                                  className="w-full px-3 py-2 text-left hover:bg-purple-50 border-b border-slate-100 last:border-0"
                                  onMouseDown={(e) => e.preventDefault()}
                                  onClick={() => selectFbo(fbo, true)}
                                >
                                  <div className="font-medium text-slate-800 text-sm">{fbo.name}</div>
                                  {fbo.address && <div className="text-xs text-slate-500">{fbo.address}</div>}
                                  {fbo.phone && <div className="text-xs text-slate-400">📞 {fbo.phone}</div>}
                                </button>
                              ))}
                            </>
                          )}
                        </div>
                      )}
                      {dropoffFbo && (
                        <div className="mt-1 text-xs text-green-600 flex items-center gap-1">
                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                          FBO selected: {dropoffFbo.name}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Tail Number (Optional) */}
                  {dropoffFbo && (
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">
                        Tail Number (Optional)
                      </label>
                      <input
                        type="text"
                        value={dropoffTailNumber}
                        onChange={(e) => setDropoffTailNumber(e.target.value.toUpperCase())}
                        placeholder="e.g., N12345"
                        className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm"
                      />
                    </div>
                  )}

                  {/* Departure Date/Time */}
                  {dropoffFbo && (
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">
                        Departure Date & Time
                      </label>
                      <input
                        type="datetime-local"
                        value={dropoffFboDateTime}
                        onChange={(e) => setDropoffFboDateTime(e.target.value)}
                        className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm"
                      />
                    </div>
                  )}
                </div>
              )}

              {/* As Directed Info Message */}
              {dropoffType === 'as_directed' && (
                <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
                  <div className="flex items-start gap-2">
                    <svg className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <div>
                      <p className="text-sm font-medium text-blue-800">Chauffeur Service - As Directed</p>
                      <p className="text-xs text-blue-700 mt-1">Passenger/s will direct the driver to the final destination.</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Location Search - Hidden for As Directed and FBO */}
              {dropoffType !== 'as_directed' && dropoffType !== 'fbo' && (
                <div className="relative">
                  <input
                    type="text"
                    value={dropoffSearch}
                    onChange={(e) => handleDropoffSearch(e.target.value)}
                    onFocus={() => dropoffSearchResults.length > 0 && setShowDropoffDropdown(true)}
                    placeholder={dropoffType === 'airport' ? 'Search by airport code or name (e.g., JFK, Los Angeles)' : 'Search address'}
                    className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  {dropoffSearchLoading && (
                    <div className="absolute right-3 top-2.5">
                      <div className="animate-spin h-5 w-5 border-2 border-blue-600 rounded-full border-t-transparent"></div>
                    </div>
                  )}
                  {showDropoffDropdown && dropoffSearchResults.length > 0 && (
                    <div className="absolute z-10 w-full mt-1 bg-white border border-slate-200 rounded-md shadow-lg max-h-60 overflow-auto">
                      {dropoffSearchResults.map((loc, idx) => (
                        <button
                          key={idx}
                          className="w-full px-3 py-2 text-left hover:bg-slate-50 border-b border-slate-100 last:border-0"
                          onClick={() => selectDropoffLocation(loc)}
                        >
                          <div className="font-medium text-slate-800">{loc.name}</div>
                          <div className="text-sm text-slate-500">{loc.details}</div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Airport-specific fields */}
            {dropoffType === 'airport' && (
              <div className="grid md:grid-cols-2 gap-4">
                {/* Airline */}
                <div className="relative">
                  <label className="block text-sm font-medium text-slate-700 mb-1">Airline</label>
                  <input
                    type="text"
                    value={dropoffAirline}
                    onChange={(e) => handleAirlineSearch(e.target.value, true)}
                    onFocus={() => dropoffAirlineSearchResults.length > 0 && setShowDropoffAirlineDropdown(true)}
                    placeholder="Search airline"
                    className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  {showDropoffAirlineDropdown && dropoffAirlineSearchResults.length > 0 && (
                    <div className="absolute z-10 w-full mt-1 bg-white border border-slate-200 rounded-md shadow-lg max-h-40 overflow-auto">
                      {dropoffAirlineSearchResults.map((airline, idx) => (
                        <button
                          key={idx}
                          className="w-full px-3 py-2 text-left hover:bg-slate-50 border-b border-slate-100 last:border-0"
                          onClick={() => selectAirline(airline, true)}
                        >
                          <span className="font-medium">{airline.code}</span> - {airline.name}
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* Flight Number */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Flight Number</label>
                  <input
                    type="text"
                    value={dropoffFlightNumber}
                    onChange={(e) => setDropoffFlightNumber(e.target.value)}
                    placeholder="e.g., 456"
                    className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                {/* Departure Date */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Departure Date</label>
                  <input
                    type="date"
                    value={dropoffDate}
                    onChange={(e) => setDropoffDate(e.target.value)}
                    className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                {/* Departure Time */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Departure Time</label>
                  <div className="flex gap-2">
                    <input
                      type="time"
                      value={dropoffTime}
                      onChange={(e) => setDropoffTime(e.target.value)}
                      className="flex-1 px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <button
                      type="button"
                      onClick={() => handleValidateFlight(true)}
                      disabled={!dropoffAirlineCode || !dropoffFlightNumber || !dropoffDate || !dropoffTime}
                      className="px-4 py-2 bg-green-600 text-white text-sm rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                      title="Validate flight and get exact departure time"
                    >
                      Validate Flight
                    </button>
                  </div>
                </div>

                {/* Destination Airport (optional) */}
                <div className="relative">
                  <label className="block text-sm font-medium text-slate-700 mb-1">Flight Destination (optional)</label>
                  <input
                    type="text"
                    value={dropoffDestinationAirport}
                    onChange={(e) => handleDestinationAirportSearch(e.target.value)}
                    onFocus={() => destinationAirportSearchResults.length > 0 && setShowDestinationAirportDropdown(true)}
                    placeholder="Search airport code or name"
                    className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  {destinationAirportSearchLoading && (
                    <div className="absolute right-3 top-8">
                      <div className="animate-spin h-5 w-5 border-2 border-blue-600 rounded-full border-t-transparent"></div>
                    </div>
                  )}
                  {showDestinationAirportDropdown && destinationAirportSearchResults.length > 0 && (
                    <div className="absolute z-10 w-full mt-1 bg-white border border-slate-200 rounded-md shadow-lg max-h-40 overflow-auto">
                      {destinationAirportSearchResults.map((loc, idx) => (
                        <button
                          key={idx}
                          className="w-full px-3 py-2 text-left hover:bg-slate-50 border-b border-slate-100 last:border-0"
                          onClick={() => selectDestinationAirport(loc)}
                        >
                          <div className="font-medium text-slate-800">{loc.id && <span className="text-blue-600">{loc.id}</span>} {loc.name}</div>
                          <div className="text-sm text-slate-500">{loc.details}</div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Passenger Section */}
          <div className="border-t border-slate-200 pt-6">
            <h3 className="text-lg font-semibold text-slate-800 mb-4">Passenger Details</h3>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Title</label>
                <select
                  value={passenger.title}
                  onChange={(e) => setPassenger({ ...passenger, title: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {TITLES.map((t) => (
                    <option key={t} value={t}>{t || '(none)'}</option>
                  ))}
                </select>
              </div>

              <div className="flex items-center">
                <label className="flex items-center gap-2 mt-6">
                  <input
                    type="checkbox"
                    checked={passenger.vip}
                    onChange={(e) => setPassenger({ ...passenger, vip: e.target.checked })}
                    className="rounded border-slate-300"
                  />
                  <span className="text-sm font-medium text-slate-700">VIP Passenger</span>
                </label>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">First Name *</label>
                <input
                  type="text"
                  value={passenger.firstName}
                  onChange={(e) => setPassenger({ ...passenger, firstName: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Last Name *</label>
                <input
                  type="text"
                  value={passenger.lastName}
                  onChange={(e) => setPassenger({ ...passenger, lastName: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Email *</label>
                <input
                  type="email"
                  value={passenger.email}
                  onChange={(e) => setPassenger({ ...passenger, email: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Mobile Phone *</label>
                <input
                  type="tel"
                  value={passenger.mobile}
                  onChange={(e) => setPassenger({ ...passenger, mobile: e.target.value })}
                  placeholder="+1 555-123-4567"
                  className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>
            </div>
          </div>

          {/* Comments */}
          <div className="border-t border-slate-200 pt-6">
            <label className="block text-sm font-medium text-slate-700 mb-1">Special Instructions (optional)</label>
            <textarea
              value={clientComments}
              onChange={(e) => setClientComments(e.target.value)}
              rows={3}
              placeholder="Any special requests or instructions for the driver..."
              className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Action Buttons */}
          <div className="border-t border-slate-200 pt-6 flex justify-end">
            <button
              onClick={handleGetRates}
              disabled={ratesLoading}
              className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {ratesLoading ? 'Getting Rates...' : 'Get Rates'}
            </button>
          </div>
        </div>
      )}

      {/* Step 2: Select Vehicle */}
      {step === 2 && (
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <h3 className="text-lg font-semibold text-slate-800 mb-4">Select Vehicle</h3>

          {rates.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              No vehicles available for this route.
            </div>
          ) : (
            <div className="space-y-4">
              {rates.map((rate, idx) => (
                <div
                  key={idx}
                  className={`p-4 border rounded-lg cursor-pointer transition-colors ${
                    selectedRate?.vehicleId === rate.vehicleId
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-slate-200 hover:border-slate-300'
                  }`}
                  onClick={() => setSelectedRate(rate)}
                >
                  <div className="flex items-start gap-4">
                    {rate.vehicleImageUrl && (
                      <img
                        src={rate.vehicleImageUrl}
                        alt={rate.vehicle}
                        className="w-24 h-16 object-contain"
                      />
                    )}
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <h4 className="font-semibold text-slate-800">{rate.vehicle}</h4>
                        <div className="text-lg font-bold text-blue-600">{rate.rate}</div>
                      </div>
                      <div className="flex items-center gap-4 text-sm text-slate-600 mt-1">
                        <span>Capacity: {rate.capacity}</span>
                        <span>Luggage: {rate.luggage}</span>
                      </div>
                      {rate.note && (
                        <p className="text-sm text-slate-500 mt-2">{rate.note}</p>
                      )}
                      {rate.surcharges && rate.surcharges.length > 0 && (
                        <div className="text-xs text-slate-500 mt-2">
                          <span className="font-medium">Note:</span> {rate.surcharges.join(', ')}
                        </div>
                      )}
                      {rate.cancellationPolicy && (
                        <div className="text-xs text-slate-500 mt-1">
                          <span className="font-medium">Cancellation:</span> {rate.cancellationPolicy}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="border-t border-slate-200 pt-6 mt-6 flex justify-between">
            <button
              onClick={() => setStep(1)}
              className="px-4 py-2 border border-slate-300 rounded-md hover:bg-slate-50"
            >
              Back
            </button>
            <button
              onClick={handleBookTrip}
              disabled={!selectedRate || bookingLoading}
              className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {bookingLoading ? 'Booking...' : 'Confirm Booking'}
            </button>
          </div>
        </div>
      )}

      {/* Step 3: Confirmation */}
      {step === 3 && bookingResult && (
        <div className="bg-white rounded-xl border border-slate-200 p-6 text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>

          <h3 className="text-xl font-semibold text-slate-800 mb-2">Booking Confirmed!</h3>
          <p className="text-slate-600 mb-4">Your chauffeur service has been booked successfully.</p>

          <div className="bg-slate-50 rounded-lg p-4 inline-block mb-6">
            <div className="text-sm text-slate-500">Confirmation Number</div>
            <div className="text-2xl font-bold text-slate-800">{bookingResult.urcn}</div>
          </div>

          {apiMode === 'test' && (
            <div className="mb-6 p-3 bg-yellow-50 border border-yellow-200 text-yellow-700 rounded-md text-sm">
              This is a TEST booking and will not result in an actual reservation.
            </div>
          )}

          <div className="flex justify-center gap-4">
            <button
              onClick={handleNewBooking}
              className="px-4 py-2 border border-slate-300 rounded-md hover:bg-slate-50"
            >
              New Booking
            </button>
            <Link
              to="/"
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              Back to Dashboard
            </Link>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChauffeuredTransferBooking;
